import type { ReactNode } from "react";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { cn } from "@/lib/utils";

export type PageBreadcrumb = { title: string; href: string | null };

export type PageHeaderProps = {
	breadcrumbItems: PageBreadcrumb[];
	title: string;
	description: ReactNode;
	/** Primary icon for the tile (include `className="size-6 text-primary"` and `aria-hidden` where decorative). */
	icon: ReactNode;
	action?: ReactNode;
	/** Optional block below the title row (same page intro region), e.g. filters or period controls. */
	after?: ReactNode;
	className?: string;
};

/**
 * Shared app page header: breadcrumbs, icon tile, title, description, optional action.
 * Line lengths follow heading-group width hints for `text-3xl` / body copy.
 */
export function PageHeader({
	breadcrumbItems,
	title,
	description,
	icon,
	action,
	after,
	className,
}: PageHeaderProps) {
	return (
		<div className={cn("mb-8", className)}>
			<Breadcrumbs items={breadcrumbItems} />
			<div className="mt-6 flex flex-wrap items-start gap-x-4 gap-y-3">
				<div className="shrink-0 rounded-lg bg-primary/10 p-2">{icon}</div>
				<div className="min-w-0 flex-1 space-y-2">
					<h1 className="max-w-[40ch] text-balance text-3xl font-semibold tracking-tight text-foreground">
						{title}
					</h1>
					<div className="max-w-[56ch] text-pretty text-base text-muted-foreground">
						{description}
					</div>
				</div>
				{action != null ? (
					<div className="flex shrink-0 items-center">{action}</div>
				) : null}
			</div>
			{after != null ? <div className="mt-6">{after}</div> : null}
		</div>
	);
}
